// React and third-party imports
import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import { DatePicker } from "rsuite";

// Local component imports
import MyComponentImportData from "./EllipsisComponentImportData.jsx";
import TagDropdown from "./components/TagDropdown.jsx";
import ProjectDropdown from "./components/ProjectDropdown.jsx";
import ProjectDisplay from "./components/ProjectDisplay.jsx";
import TagDisplay from "./components/TagDisplay.jsx";
import TimeDisplay from "./components/TimeDisplay.jsx";
import TimeInputs from "./components/TimeInputs.jsx";
import DatePickerIcon from "./components/DatePickerIcon.jsx";
import DurationDisplay from "./components/DurationDisplay.jsx";
import DurationInput from "./components/DurationInput.jsx";
import UpdateButton from "./components/UpdateButton.jsx";
import ActionsMenu from "./components/ActionsMenu.jsx";

// Helper imports
import zoneConverter from "../helpers/zoneConverter.jsx";
import {
  formatChange,
  convertTo24HourFormat,
  formatTimeTo24Hr,
  convertTo12,
  isEndTimeGreater,
  isValidTimeFormat,
  convertTo24Hour,
  validateNoAlphabets,
} from "../services/timeFormat.js";
import {
  parseTimeInput,
  timeFormatChange,
  formatTime,
  validateTimeFormat,
  getTimeComponents,
  convertTimeStamp,
} from "../services/timeValidation.js";
import { groupAndSumDurations } from "../services/dataProcessing.js";
import {
  duplicateTimerEntry,
  updateTimerEntry,
  deleteTimerEntry,
} from "../services/apiHelpers.js";
import {
  create12HourTimeHandlers,
  create24HourTimeHandlers,
  handleTimeShowEdit,
} from "../services/timeHandlers.js";
import {
  createProjectDropdownToggleHandler,
  createProjectFilterChangeHandler,
  createClientToggleHandler,
  createProjectSelectHandler,
  createProjectToggleHandler,
  createSprintSelectHandler,
  createTagInputChangeHandler,
  createTagSelectHandler,
  createDropdownToggleHandler,
} from "../services/dropdownHandlers.js";
import { filterClientData, filterTagItems } from "../services/dataFilters.js";
import { initializeEditState, resetEditState, createEditStateSetters, createGroupClickHandler } from "../services/editStateHelpers.js";
import { BillableIcon, formatDuration } from "../services/uiHelpers.js";
import { useClickOutsideTimeInputs } from "./hooks/useClickOutsideTimeInputs.js";

// API hooks
import { useTimerDataImportData } from "../TimerDataImportDataApi.jsx";
import { useClientProjectDetails, useTagDetails } from "../../../Timer/tracker/TimerDataApi.jsx";

// Redux actions
import { setTimerData } from "../../../../../features/timerData/timerDataSlice.js";
const CardBodyImportData = ({ item, timeFormat, globalEditedId, setGlobalEditedId }) => {
  const [deleteId, setDeleteId] = useState("");
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  
  // Use global editedId if provided, otherwise maintain local state for backward compatibility
  const [localEditedId, setLocalEditedId] = useState(null);
  const editedId = globalEditedId !== undefined ? globalEditedId : localEditedId;
  const effectiveSetEditedId = setGlobalEditedId || setLocalEditedId;
  const [taskNameEdit, setTaskNameEdit] = useState("");
  const [dateEdit, setDateEdit] = useState(new Date());
  const [startTimeEdit, setStartTimeEdit] = useState("");
  const [startTimeTemp, setStartTimeTemp] = useState("");
  const [startTimeEdit24, setStartTimeEdit24] = useState("");
  const [endTimeEdit, setEndTimeEdit] = useState("");
  const [endTimeTemp, setEndTimeTemp] = useState("");
  const [endTimeEdit24, setEndTimeEdit24] = useState("");
  const [clientEdit, setClientEdit] = useState(null);
  const [clientIdEdit, setClientIdEdit] = useState(null);
  const [projectNameEdit, setProjectNameEdit] = useState("");
  const [projectIdEdit, setProjectIdEdit] = useState("");
  const [dateOldEdit, setDateOldEdit] = useState(null);
  const [tagIdEdit, setTagIdEdit] = useState(null);
  const [billableEdit, setBillableEdit] = useState(false);
  const [uDuration, setUDuration] = useState(null);
  const [sprintIdEdit, setSprintIdEdit] = useState("");
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);
  const [uEndTimeError, setUEndTimeError] = useState(false);
  const [uStartTimeError, setUStartTimeError] = useState(false);
  const [sprintNameEdit, setSprintNameEdit] = useState("");
  const [projectEditDropdownOpen, setProjectEditDropdownOpen] = useState(false);
  const [tagEditDropdownOpen, setTagEditDropdownOpen] = useState(false);
  const [uFormatedTime, setUFormatedTime] = useState(0);
  const [uFormatedTimeError, setUFormatedTimeError] = useState(false);
  const [isTimeInputActive, setIsTimeInputActive] = useState(false);
  const [filter, setFilter] = useState("");
  const [selectedClients, setSelectedClients] = useState(new Set());
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeDropdownId, setActiveDropdownId] = useState(null);
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [tagEdit, setTagEdit] = useState(null);
  const [tagQuery, setTagQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [groupedData, setGroupedData] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState([]);
  // ==================== Refs ====================
  const datePickerRef = useRef();
  const projectEditDropdownRef = useRef(null);
  const projectDropdownContentRef = useRef(null);
  const tagEditDropdownRef = useRef(null);
  const tagDropdownContentRef = useRef(null);
  const startInputRef = useRef(null);
  const endInputRef = useRef(null);
  const startRowInputRef = useRef(null);
  const endRowInputRef = useRef(null);
  const start12TimeInputRef = useRef(null);
  const end12TimeInputRef = useRef(null);
  const start12TimeRowInputRef = useRef(null);
  const end12TimeRowInputRef = useRef(null);
  const activedropdownRefs = useRef({});

  // ==================== Redux & Hooks ====================
  const dispatch = useDispatch();
  const { appSettings } = useSelector((state) => state.appSettings);
  const token = useSelector((state) => state.auth.token);
  const timerData = useSelector((state) => state.timerData.timerDataState);
  const { refetch: refetchTimerData } = useTimerDataImportData(token);
  const { data: clientData = [] } = useClientProjectDetails(token);
  const { data: tagOptions = [] } = useTagDetails(token);

  // ==================== Helper Functions ====================
  const currentTextCount = (text1, text2, text3) => {
    const text = `${text1} ${text2 ? "-" + text2 : ""} ${text3 ? text3 : ""}`;
    return text.length;
  };

  // Get className based on text count
  const getTextSizeClassName = (projectName, sprintName, clientName) => {
    const count = currentTextCount(projectName, sprintName, clientName);
    return count < 21 ? "small" : "large";
  };

  // Create edit state setters object once to reduce repetition
  // Use global setter if available, otherwise use local setter
  const editStateSetters = createEditStateSetters({
    setEditedId: effectiveSetEditedId,
    setTaskNameEdit,
    setProjectIdEdit,
    setProjectNameEdit,
    setSelectedUser,
    setSprintNameEdit,
    setSprintIdEdit,
    setDateEdit,
    setTagIdEdit,
    setStartTimeEdit,
    setEndTimeEdit,
    setStartTimeTemp,
    setEndTimeTemp,
    setStartTimeEdit24,
    setEndTimeEdit24,
    setClientEdit,
    setClientIdEdit,
    setBillableEdit,
    setUFormatedTime,
    setUDuration,
    setUStartTimeError,
    setUEndTimeError,
    setUFormatedTimeError,
    setProjectEditDropdownOpen,
    setTagEditDropdownOpen,
  });

  const toggleGroup = (groupIndex) => {
    effectiveSetEditedId(null);
    setExpandedGroups((prevGroups) => {
      if (prevGroups.includes(groupIndex)) {
        return prevGroups.filter((index) => index !== groupIndex);
      } else {
        return [...prevGroups, groupIndex];
      }
    });
  };

  // Helper functions for duplicate/delete actions
  const handleDuplicate = (entry) => {
    if (!duplicateLoading) {
      setSelectedUser(entry.user);
      const projectId = entry.project?._id;
      const tagId = entry.tag?._id;
      const clientName = entry.project?.client_name;
      const duration = entry.totalDuration || entry.duration;
      duplicate(
        entry.task,
        projectId,
        tagId,
        entry.client,
        clientName,
        entry.date,
        entry.startTime,
        entry.endTime,
        duration,
        entry.sprint ? entry.sprint.sprint_name : null,
        entry.sprint ? entry.sprint._id : null,
        entry._id,
        entry.billable
      );
    }
  };

  const handleDelete = (entryId, entry) => {
    setSelectedUser(entry.user);
    if (!isDeleteLoading) {
      handleResetClick(entryId, entry);
    }
  };

  const duplicate = async (
    task,
    project_id,
    tag_id,
    client,
    clientId,
    date,
    startTime,
    dupendTime,
    duration,
    sprint_name,
    sprint_id,
    _id,
    billable
  ) => {
    await duplicateTimerEntry({
      task,
      project_id,
      tag_id,
      client,
      clientId,
      date,
      startTime,
      endTime: dupendTime,
      duration,
      sprint_name,
      sprint_id,
      _id,
      billable,
      selectedUser,
      token,
      dispatch,
      setTimerData,
      setDuplicateLoading,
      setActiveDropdownId,
    });
  };
  // ==================== Effects ====================
  useEffect(() => {
    refetchTimerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedGroups, selectedUser]);

  // ==================== Utility Functions ====================
  const convertTimeStampHelper = convertTimeStamp;

  // Default time values
  const DEFAULT_START_TIME = {
    24: "7:46pm",
    12: "07:46 PM",
  };
  const DEFAULT_END_TIME = {
    24: "8:50pm",
    12: "08:50 PM",
  };

  // Tag dropdown toggle handler
  const handleTagDropdownToggle = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setTagEditDropdownOpen((prev) => !prev);
    setProjectEditDropdownOpen(false);
  };

  // ==================== Dropdown Handlers ====================
  const handleTagInputChange = createTagInputChangeHandler(setTagQuery, setTagIdEdit);
  const handleTagSelect = createTagSelectHandler(setTagEdit, setTagIdEdit, setTagEditDropdownOpen, setTagQuery);
  const handleProjectDropdownToggle = createProjectDropdownToggleHandler({
    setProjectEditDropdownOpen,
    setFilter,
    setSelectedClients,
    setSelectedProject,
    setTagEditDropdownOpen,
    setUStartTimeError,
    setUEndTimeError,
    setUFormatedTimeError,
  });
  const handleDropdownToggle = createDropdownToggleHandler(setActiveDropdownId);
  const filteredClientData = filterClientData(clientData, filter);

  const handleProjectFilterChange = createProjectFilterChangeHandler(
    setFilter,
    setSelectedClients,
    clientData
  );
  const handleClientToggle = createClientToggleHandler(setSelectedClients);
  const handleProjectSelect = createProjectSelectHandler({
    setSprintIdEdit,
    setSprintNameEdit,
    setProjectIdEdit,
    setProjectNameEdit,
    setClientEdit,
    setClientIdEdit,
    setBillableEdit,
    setProjectEditDropdownOpen,
  });
  const handleProjectToggle = createProjectToggleHandler(setSelectedProject, selectedProject);
  const handleSprintSelect = createSprintSelectHandler({
    setSprintIdEdit,
    setSprintNameEdit,
    setProjectIdEdit,
    setProjectNameEdit,
    setClientEdit,
    setClientIdEdit,
    setBillableEdit,
    setProjectEditDropdownOpen,
  });

  const handleResetClick = async (taskId, group) => {
    await deleteTimerEntry({
      taskId,
      userId: selectedUser,
      token,
      setIsDeleteLoading,
      setDeleteId,
      refetchTimerData,
      dispatch,
      setTimerData,
      group,
      setExpandedGroups,
      handleDropdownToggle: () => handleDropdownToggle(taskId),
    });
  };

  const updateTimer2 = async (event) => {
    event.preventDefault();
    await updateTimerEntry({
      id: editedId,
      task: taskNameEdit,
      dateEdit: new Date(dateEdit),
      startTimeEdit,
      endTimeEdit,
      clientEdit,
      clientIdEdit,
      projectIdEdit,
      sprintIdEdit,
      tagIdEdit,
      billableEdit,
      uDuration,
      dateOldEdit,
      selectedUser,
      token,
      dispatch,
      setTimerData,
      setIsUpdateLoading,
      setExpandedGroups,
      refetchTimerData,
      resetEditState,
      resetSetters: {
        setEditedId: effectiveSetEditedId,
        setTaskNameEdit,
        setProjectIdEdit,
        setProjectNameEdit,
        setSelectedUser,
        setSprintNameEdit,
        setSprintIdEdit,
        setDateEdit,
        setTagIdEdit,
        setStartTimeEdit,
        setEndTimeEdit,
        setClientEdit,
        setClientIdEdit,
        setBillableEdit,
        setUFormatedTime,
        setUDuration,
      },
    });
    setUEndTimeError(false);
    setUStartTimeError(false);
  };

  const filteredTagItems = filterTagItems(tagOptions, tagQuery);

  // Calculate duration when start/end times change (12-hour format)
  // Uses startTimeTemp/endTimeTemp as dependencies to trigger after user finishes editing
  useEffect(() => {
    if (startTimeEdit && endTimeEdit) {
      const timeString1 = startTimeEdit.trim();
      const timeString2 = endTimeEdit.trim();
      const format = "h:mm A";
      const time1 = moment(timeString1, format).tz(global.config.getTimeZone());
      const time2 = moment(timeString2, format).tz(global.config.getTimeZone());
      const timeDifferenceInSeconds = time2.diff(time1, "seconds");

      if (timeDifferenceInSeconds > 0) {
        setUDuration(timeDifferenceInSeconds);
        setUFormatedTime(formatDuration(timeDifferenceInSeconds, formatTime));
        setUStartTimeError(false);
        setUEndTimeError(false);
        setUFormatedTimeError(false);
      } else {
        setUStartTimeError(true);
        setUEndTimeError(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTimeTemp, endTimeTemp]);

  // Time change handlers
  const { changeStartTime, changeEndTime } = create12HourTimeHandlers({
    setStartTimeEdit,
    setStartTimeTemp,
    setEndTimeEdit,
    setEndTimeTemp,
    setUStartTimeError,
    setUEndTimeError,
  });

  const { changeStartTime24, changeEndTime24 } = create24HourTimeHandlers({
    setStartTimeEdit24,
    setStartTimeEdit,
    setEndTimeEdit24,
    setEndTimeEdit,
    setUStartTimeError,
    setUEndTimeError,
    setUFormatedTimeError,
    setUDuration,
    setUFormatedTime,
    getEndTimeEdit24: () => endTimeEdit24,
    getStartTimeEdit24: () => startTimeEdit24,
    formatTime,
  });

  // Handle click outside time inputs
  useClickOutsideTimeInputs({
    startInputRef,
    endInputRef,
    startRowInputRef,
    endRowInputRef,
    start12TimeInputRef,
    end12TimeInputRef,
    start12TimeRowInputRef,
    end12TimeRowInputRef,
    changeStartTime24,
    changeEndTime24,
    changeStartTime,
    changeEndTime,
    startTimeEdit,
    endTimeEdit,
    startTimeTemp,
    endTimeTemp,
  });

  const handleDateIconClick = () => {
    datePickerRef.current.open(); // Open the date picker programmatically
  };

  const timeShowEdit = (timeSec) => {
    handleTimeShowEdit({
      timeSec,
      dateEdit,
      startTimeEdit,
      setUDuration,
      setEndTimeEdit,
      setUFormatedTime,
      setUFormatedTimeError,
      setUStartTimeError,
      setUEndTimeError,
      convertTimeStamp: convertTimeStampHelper,
    });
  };

  // ==================== Effects ====================
  useEffect(() => {
    if (item && item[1]) {
      const updatedGroupedData = groupAndSumDurations(item[1]);
      setGroupedData(updatedGroupedData);
    }
  }, [item, isUpdateLoading, timerData]);

  // Sync with global editedId - close edit mode if globalEditedId is set to a different entry
  useEffect(() => {
    if (globalEditedId !== undefined && setGlobalEditedId) {
      // Get all entry IDs in this card
      const entryIds = new Set();
      if (groupedData && groupedData.length > 0) {
        groupedData.forEach((group) => {
          entryIds.add(group._id);
          if (group.entries && group.entries.length > 0) {
            group.entries.forEach((row) => {
              entryIds.add(row._id);
            });
          }
        });
      }

      // If globalEditedId is set but doesn't match any entry in this card, reset local edit state
      if (globalEditedId !== null && !entryIds.has(globalEditedId)) {
        // Reset all edit-related state when another card's entry is being edited
        setProjectEditDropdownOpen(false);
        setTagEditDropdownOpen(false);
        setUEndTimeError(false);
        setUStartTimeError(false);
        setUFormatedTime("");
      }
    }
  }, [globalEditedId, groupedData, setGlobalEditedId]);

  useEffect(() => {
    refetchTimerData();
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        document.activeElement.blur();
        effectiveSetEditedId(null);
        setProjectEditDropdownOpen(false);
        setTagEditDropdownOpen(false);
        setUEndTimeError(false);
        setUStartTimeError(false);
        setUFormatedTime("");
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [timerData, refetchTimerData, effectiveSetEditedId]);

  useEffect(() => {
    const handleClickOutsideDropdown = (event) => {
      const isClickInsideActiveDropdown = activeDropdownId && activedropdownRefs.current[activeDropdownId] && !activedropdownRefs.current[activeDropdownId].contains(event.target);

      if (isClickInsideActiveDropdown) {
        setActiveDropdownId(null);
      }
    };

    document.addEventListener("click", handleClickOutsideDropdown);
    return () => {
      document.removeEventListener("click", handleClickOutsideDropdown);
    };
  }, [activeDropdownId]);

  // Close project/tag dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const projectNode = projectEditDropdownRef.current;
      const projectDropdownContent = projectDropdownContentRef.current;
      const tagNode = tagEditDropdownRef.current;
      const tagDropdownContent = tagDropdownContentRef.current;

      // Check if click is outside project dropdown (both container and dropdown content)
      if (projectEditDropdownOpen) {
        const isClickInsideProjectContainer = projectNode && projectNode.contains(event.target);
        const isClickInsideDropdown = projectDropdownContent && projectDropdownContent.contains(event.target);

        if (!isClickInsideProjectContainer && !isClickInsideDropdown) {
          setProjectEditDropdownOpen(false);
          setFilter("");
          setSelectedClients(new Set());
          setSelectedProject(null);
        }
      }

      // Check if click is outside tag dropdown (both container and dropdown content)
      if (tagEditDropdownOpen) {
        const isClickInsideTagContainer = tagNode && tagNode.contains(event.target);
        const isClickInsideTagDropdown = tagDropdownContent && tagDropdownContent.contains(event.target);

        // Don't close if clicking inside the tag container or dropdown content
        if (!isClickInsideTagContainer && !isClickInsideTagDropdown) {
          setTagEditDropdownOpen(false);
        }
      }
    };

    // Only add listener if dropdown is open
    if (projectEditDropdownOpen || tagEditDropdownOpen) {
      // Use a small timeout to ensure the refs are set after render
      // This prevents the handler from firing immediately when opening the dropdown
      const timeoutId = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 10);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [projectEditDropdownOpen, tagEditDropdownOpen]);

  // Sync 24-hour format when 12-hour format changes and validate times
  useEffect(() => {
    if (startTimeEdit && endTimeEdit) {
      const timeString1 = startTimeEdit.trim();
      const timeString2 = endTimeEdit.trim();
      const format = "h:mm A";
      const time1 = moment(timeString1, format).tz(global.config.getTimeZone());
      const time2 = moment(timeString2, format).tz(global.config.getTimeZone());
      const timeDifferenceInSeconds = time2.diff(time1, "seconds");

      if (timeDifferenceInSeconds > 0) {
        setUStartTimeError(false);
        setUEndTimeError(false);
        setUFormatedTimeError(false);
      } else {
        setUStartTimeError(true);
        setUEndTimeError(true);
      }
    }
    if (startTimeEdit) {
      setStartTimeEdit24(convertTo24Hour(startTimeEdit));
    }
    if (endTimeEdit) {
      setEndTimeEdit24(convertTo24Hour(endTimeEdit));
    }
  }, [startTimeEdit, endTimeEdit]);

  return (
    <Card.Body className="body-mg">
      <>
        {groupedData
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .map((group, groupIndex) => (
            <React.Fragment key={groupIndex}>
              <form onSubmit={updateTimer2}>
                <>
                  <input type="hidden" name="id" value={group._id} />
                  {/* start */}
                  <div
                    className={`timetracker-inner data-time-tracker overview-sec-timer date-${group._id} ${group._id === editedId && group.entries.length === 1 ? "editer" : ""}`}
                    style={{
                      backgroundColor: group._id === editedId && group.entries.length === 1 && "#f0f7f9",
                      opacity: group._id === editedId && group.entries.length === 1 && "1",
                    }}
                  >
                    <div className="merge-tracker">
                      <div className="overview-sec-timer-inner">
                        <div className="project-name">
                          {group.entries.length > 1 && (
                            <div onClick={() => toggleGroup(groupIndex)} style={{ cursor: "pointer" }} className="count-merge" >
                              <span>{group.entries.length}</span>
                            </div>
                          )}

                          <Col
                            className={"cursor"}
                            onClick={createGroupClickHandler(
                              group,
                              groupIndex,
                              initializeEditState,
                              editStateSetters,
                              formatTime,
                              toggleGroup
                            )}
                          >
                            {group._id === editedId && group.entries.length === 1 ? (
                              <input
                                type="text"
                                name="task"
                                value={taskNameEdit}
                                onChange={(e) => setTaskNameEdit(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                placeholder="Regular Input"
                                className={group._id === editedId && group.entries.length === 1 ? "input-edit-project" : "input-bg input-edit-project"}
                                style={{ textOverflow: "ellipsis" }}
                                autoComplete="off"
                              />
                            ) : group._id === editedId &&
                              group.entries.length > 1 ? (
                              <React.Fragment>
                                {group.task.length > 20 && (
                                  <span className="tooltip-tracker testq">
                                    {group.task}
                                  </span>
                                )}

                                <input
                                  type="text"
                                  name="task"
                                  value={group.task || ""}
                                  placeholder="Regular Input"
                                  style={{ textOverflow: "ellipsis" }}
                                  autoComplete="off"
                                  readOnly
                                />
                              </React.Fragment>
                            ) : null}

                            {group._id !== editedId && (
                              <MyComponentImportData group={group} editedId={editedId} />
                            )}
                          </Col>
                        </div>

                        <div
                          className="project-sprint-outer col"
                          ref={projectEditDropdownRef}
                        >
                          {group._id === editedId && group.entries.length === 1 ? (
                            <ProjectDropdown
                              isOpen={projectEditDropdownOpen}
                              onToggle={handleProjectDropdownToggle}
                              filter={filter}
                              onFilterChange={handleProjectFilterChange}
                              filteredClientData={filteredClientData}
                              selectedClients={selectedClients}
                              onClientToggle={handleClientToggle}
                              selectedProject={selectedProject}
                              onProjectSelect={handleProjectSelect}
                              onProjectToggle={handleProjectToggle}
                              onSprintSelect={handleSprintSelect}
                              projectNameEdit={projectNameEdit}
                              sprintNameEdit={sprintNameEdit}
                              clientEdit={clientEdit}
                              projectIdEdit={projectIdEdit}
                              currentTextCount={currentTextCount}
                              isMobile={false}
                              contentRef={projectDropdownContentRef}
                            />
                          ) : group._id === editedId &&
                            group.entries.length > 1 ? (
                            <ProjectDisplay
                              project={group.project}
                              sprint={group.sprint}
                              client={group.client}
                              currentTextCount={currentTextCount}
                              onClick={createGroupClickHandler(
                                group,
                                groupIndex,
                                initializeEditState,
                                editStateSetters,
                                formatTime,
                                toggleGroup
                              )}
                            />
                          ) : null}

                          {group._id !== editedId && (
                            <ProjectDisplay
                              project={group.project}
                              sprint={group.sprint}
                              client={group.client}
                              currentTextCount={currentTextCount}
                              onClick={() => {
                                if (group.entries.length === 1) {
                                  initializeEditState(group, editStateSetters, formatTime);
                                } else {
                                  toggleGroup(groupIndex);
                                }
                              }}
                            />
                          )}
                        </div>

                        {/* mobile view */}
                        <ActionsMenu
                          itemId={group._id}
                          isOpen={activeDropdownId === group._id}
                          onToggle={() => handleDropdownToggle(group._id)}
                          onDuplicate={() => handleDuplicate(group)}
                          onDelete={() => handleDelete(group._id, group)}
                          duplicateLoading={duplicateLoading}
                          deleteLoading={isDeleteLoading}
                          deleteId={deleteId}
                          style={{ display: "none" }}
                          refCallback={(el) => (activedropdownRefs.current[group._id] = el)}
                        />

                        {/* mobile view */}
                      </div>

                      <div className="outer-right-sec-overview">
                        <div className="overview-sec-timer-inner-right">
                          {/* Mobile view */}

                          <div
                            className="project-sprint-outer col project-sprint-outer-mobile"
                            style={{ display: "none" }}
                            ref={projectEditDropdownRef}
                          >
                            {group._id === editedId &&
                              group.entries.length === 1 ? (
                              <ProjectDropdown
                                isOpen={projectEditDropdownOpen}
                                onToggle={handleProjectDropdownToggle}
                                filter={filter}
                                onFilterChange={handleProjectFilterChange}
                                filteredClientData={filteredClientData}
                                selectedClients={selectedClients}
                                onClientToggle={handleClientToggle}
                                selectedProject={selectedProject}
                                onProjectSelect={handleProjectSelect}
                                onProjectToggle={handleProjectToggle}
                                onSprintSelect={handleSprintSelect}
                                projectNameEdit={projectNameEdit}
                                sprintNameEdit={sprintNameEdit}
                                clientEdit={clientEdit}
                                projectIdEdit={projectIdEdit}
                                currentTextCount={currentTextCount}
                                isMobile={true}
                                contentRef={projectDropdownContentRef}
                              />
                            ) : group._id === editedId &&
                              group.entries.length > 1 ? (
                              <ProjectDisplay
                                project={group.project}
                                sprint={group.sprint}
                                client={group.client}
                                currentTextCount={currentTextCount}
                                onClick={createGroupClickHandler(
                                  group,
                                  groupIndex,
                                  initializeEditState,
                                  editStateSetters,
                                  formatTime,
                                  toggleGroup
                                )}
                                isMobile={true}
                              />
                            ) : null}

                            {group._id !== editedId && (
                              <ProjectDisplay
                                project={group.project}
                                sprint={group.sprint}
                                client={group.client}
                                currentTextCount={currentTextCount}
                                onClick={() => {
                                  if (group.entries.length === 1) {
                                    initializeEditState(group, editStateSetters, formatTime);
                                  } else {
                                    toggleGroup(groupIndex);
                                  }
                                }}
                                isMobile={true}
                              />
                            )}
                          </div>

                          {/* end mobile */}

                          {/* tag , timer section */}
                          <div className="tag-section-mobile-section">
                            <div className="tag-section-overview-outer dddd" ref={tagEditDropdownRef}>
                              {group._id === editedId &&
                                group.entries.length === 1 ? (
                                <>
                                  <TagDisplay
                                    tag={group.tag}
                                    tagEdit={tagEdit}
                                    onClick={handleTagDropdownToggle}
                                  />
                                  <TagDropdown
                                    isOpen={tagEditDropdownOpen}
                                    tagEdit={tagEdit}
                                    tagQuery={tagQuery}
                                    onTagInputChange={handleTagInputChange}
                                    filteredTagItems={filteredTagItems}
                                    onTagSelect={handleTagSelect}
                                    contentRef={tagDropdownContentRef}
                                  />
                                </>
                              ) : group._id === editedId &&
                                group.entries.length > 1 ? (
                                <TagDisplay
                                  tag={group.tag}
                                  onClick={createGroupClickHandler(
                                    group,
                                    groupIndex,
                                    initializeEditState,
                                    editStateSetters,
                                    formatTime,
                                    toggleGroup
                                  )}
                                />
                              ) : null}

                              {group._id !== editedId && (
                                <TagDisplay
                                  tag={group.tag}
                                  onClick={createGroupClickHandler(
                                    group,
                                    groupIndex,
                                    initializeEditState,
                                    editStateSetters,
                                    formatTime,
                                    toggleGroup
                                  )}
                                />
                              )}
                              <input
                                type="hidden"
                                name="tag"
                                value={tagIdEdit || ""}
                              />
                            </div>

                            <div className="billable-section">
                              <BillableIcon isBillable={editedId === group._id ? billableEdit : group.billable} />
                            </div>
                            <div
                              className={`overview-sec-timer-date-time date-${group._id}`}
                              style={{ position: "relative" }}
                            >
                              <Col>
                                {group._id === editedId &&
                                  group.entries.length === 1 ? (
                                  <>
                                    <div onClick={(e) => e.stopPropagation()}>
                                      <TimeInputs
                                        timeFormat={timeFormat}
                                        startTimeEdit24={startTimeEdit24}
                                        endTimeEdit24={endTimeEdit24}
                                        startTimeEdit={startTimeEdit}
                                        endTimeEdit={endTimeEdit}
                                        onStartTimeChange24={(e) => {
                                          if (validateNoAlphabets(e.target.value)) {
                                            setStartTimeEdit24(e.target.value);
                                          }
                                        }}
                                        onEndTimeChange24={(e) => {
                                          if (validateNoAlphabets(e.target.value)) {
                                            setEndTimeEdit24(e.target.value);
                                          }
                                        }}
                                        onStartTimeChange={(e) => setStartTimeEdit(e.target.value)}
                                        onEndTimeChange={(e) => setEndTimeEdit(e.target.value)}
                                        onStartTimeBlur={() => {
                                          if (!startTimeEdit) setStartTimeEdit(DEFAULT_START_TIME[timeFormat] || DEFAULT_START_TIME[12]);
                                          setIsTimeInputActive(false);
                                        }}
                                        onEndTimeBlur={() => {
                                          if (!endTimeEdit) setEndTimeEdit(DEFAULT_END_TIME[timeFormat] || DEFAULT_END_TIME[12]);
                                          setIsTimeInputActive(false);
                                        }}
                                        onStartTimeFocus={() => setIsTimeInputActive(true)}
                                        onEndTimeFocus={() => setIsTimeInputActive(true)}
                                        uStartTimeError={uStartTimeError}
                                        uEndTimeError={uEndTimeError}
                                        startInputRef={startInputRef}
                                        endInputRef={endInputRef}
                                        start12TimeInputRef={start12TimeInputRef}
                                        end12TimeInputRef={end12TimeInputRef}
                                        editedId={editedId}
                                        groupId={group._id}
                                      />
                                    </div>

                                    <div className="date-overview-sec date-overview-edit" onClick={(e) => e.stopPropagation()}>
                                      <DatePickerIcon onClick={handleDateIconClick} />
                                      <DatePicker
                                        block
                                        name="date"
                                        appearance="subtle"
                                        value={
                                          new Date(
                                            zoneConverter(
                                              dateEdit,
                                              global.config.getTimeZone(),
                                              "YYYY-MM-DD HH:mm:ss"
                                            )
                                          )
                                        }
                                        onChange={(value) => {
                                          setDateOldEdit(
                                            new Date(
                                              zoneConverter(
                                                dateEdit,
                                                global.config.getTimeZone(),
                                                "YYYY-MM-DD HH:mm:ss"
                                              )
                                            )
                                          );
                                          setDateEdit(value);
                                        }}
                                        ref={datePickerRef}
                                        placement="bottomEnd"
                                        oneTap
                                        isoWeek={
                                          appSettings.weekStartDay &&
                                          appSettings.weekStartDay === "Monday"
                                        }
                                      />
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <TimeDisplay
                                      startTime={group.startTime}
                                      endTime={group.endTime}
                                      timeFormat={timeFormat}
                                      onClick={createGroupClickHandler(
                                        group,
                                        groupIndex,
                                        initializeEditState,
                                        editStateSetters,
                                        formatTime,
                                        toggleGroup
                                      )}
                                    />
                                    <div
                                      className="date-overview-sec"
                                      onClick={createGroupClickHandler(
                                        group,
                                        groupIndex,
                                        initializeEditState,
                                        editStateSetters,
                                        formatTime,
                                        toggleGroup
                                      )}
                                    >
                                      <DatePickerIcon style={{ cursor: "pointer" }} />
                                    </div>
                                  </>
                                )}
                              </Col>
                            </div>

                            <div className="overview-time-edit">
                              <Col>
                                <div className="overview-time-edit-inner">
                                  {/* duration edit section */}
                                  {group._id === editedId &&
                                    group.entries.length === 1 ? (
                                    <div onClick={(e) => e.stopPropagation()}>
                                      <DurationInput
                                        value={uFormatedTime}
                                        onChange={(e) => timeShowEdit(e.target.value)}
                                        error={uFormatedTimeError}
                                      />
                                    </div>
                                  ) : group._id === editedId &&
                                    group.entries.length > 1 ? (
                                    <DurationDisplay
                                      duration={group.totalDuration}
                                      formatTime={formatTime}
                                      onClick={createGroupClickHandler(
                                        group,
                                        groupIndex,
                                        initializeEditState,
                                        editStateSetters,
                                        formatTime,
                                        toggleGroup
                                      )}
                                    />
                                  ) : null}

                                  {/* duration edit section end */}

                                  {group._id !== editedId && (
                                    <DurationDisplay
                                      duration={group.totalDuration}
                                      formatTime={formatTime}
                                      onClick={createGroupClickHandler(
                                        group,
                                        groupIndex,
                                        initializeEditState,
                                        editStateSetters,
                                        formatTime,
                                        toggleGroup
                                      )}
                                    />
                                  )}
                                </div>

                                {group._id === editedId &&
                                  group.entries.length === 1 ? (
                                  <UpdateButton
                                    isLoading={isUpdateLoading}
                                    disabled={
                                      isUpdateLoading ||
                                      uFormatedTimeError ||
                                      uEndTimeError ||
                                      uStartTimeError ||
                                      isTimeInputActive
                                    }
                                    isTimeInputActive={isTimeInputActive}
                                  />
                                ) : null}

                                {group._id !== editedId &&
                                  group.entries.length < 2 && (
                                    <ActionsMenu
                                      itemId={group._id}
                                      isOpen={activeDropdownId === group._id}
                                      onToggle={() => handleDropdownToggle(group._id)}
                                      onDuplicate={() => handleDuplicate(group)}
                                      onDelete={() => handleDelete(group._id, group)}
                                      duplicateLoading={duplicateLoading}
                                      deleteLoading={isDeleteLoading}
                                      deleteId={deleteId}
                                      refCallback={(el) => (activedropdownRefs.current[group._id] = el)}
                                    />
                                  )}
                              </Col>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {expandedGroups.includes(groupIndex) &&
                      group.entries.length > 1 &&
                      group.entries.map((row) => (
                        <React.Fragment key={row._id}>
                          <div
                            className={`merge-tracker merge-tracker-iner ${row._id === editedId ? "editer" : ""}`}
                            style={{
                              backgroundColor:
                                row._id === editedId ? "#f0f7f9" : "#fff",
                              padding: row._id === editedId ? "10px" : "",
                            }}
                          >
                            <div className="overview-sec-timer-inner">
                              <div className="project-name">
                                <div
                                  className="count-merge"
                                  style={{ cursor: "pointer" }}
                                >
                                  <span></span>
                                </div>
                                <Col
                                  className={"cursor"}
                                  onClick={() => {
                                    initializeEditState(row, editStateSetters, formatTime);
                                  }}
                                >
                                  {row._id === editedId && (
                                    <input
                                      type="text"
                                      name="task"
                                      value={taskNameEdit}
                                      onChange={(e) =>
                                        setTaskNameEdit(e.target.value)
                                      }
                                      onClick={(e) => e.stopPropagation()}
                                      placeholder="Regular Input"
                                      className={
                                        row._id === editedId
                                          ? "input-edit-project"
                                          : "input-bg input-edit-project"
                                      }
                                      style={{ textOverflow: "ellipsis" }}
                                      autoComplete="off"
                                    />
                                  )}

                                  {row._id !== editedId && (
                                    <MyComponentImportData
                                      group={row}
                                      editedId={editedId}
                                    />
                                  )}
                                </Col>
                              </div>

                              <div
                                className="project-sprint-outer col"
                                ref={projectEditDropdownRef}
                              >
                                {row._id === editedId && (
                                  <ProjectDropdown
                                    isOpen={projectEditDropdownOpen}
                                    onToggle={handleProjectDropdownToggle}
                                    filter={filter}
                                    onFilterChange={handleProjectFilterChange}
                                    filteredClientData={filteredClientData}
                                    selectedClients={selectedClients}
                                    onClientToggle={handleClientToggle}
                                    selectedProject={selectedProject}
                                    onProjectSelect={handleProjectSelect}
                                    onProjectToggle={handleProjectToggle}
                                    onSprintSelect={handleSprintSelect}
                                    projectNameEdit={projectNameEdit}
                                    sprintNameEdit={sprintNameEdit}
                                    clientEdit={clientEdit}
                                    projectIdEdit={projectIdEdit}
                                    currentTextCount={currentTextCount}
                                    isMobile={false}
                                    contentRef={projectDropdownContentRef}
                                  />
                                )}

                                {row._id !== editedId && (
                                  <ProjectDisplay
                                    project={row.project}
                                    sprint={row.sprint}
                                    client={row.client}
                                    currentTextCount={currentTextCount}
                                    onClick={() => {
                                      initializeEditState(row, editStateSetters, formatTime);
                                    }}
                                  />
                                )}
                              </div>

                              {/* mobile view */}
                              <ActionsMenu
                                itemId={row._id}
                                isOpen={activeDropdownId === row._id}
                                onToggle={() => handleDropdownToggle(row._id)}
                                onDuplicate={() => handleDuplicate(row)}
                                onDelete={() => handleDelete(row._id, group)}
                                duplicateLoading={duplicateLoading}
                                deleteLoading={isDeleteLoading}
                                deleteId={deleteId}
                                style={{ display: "none" }}
                                refCallback={(el) => (activedropdownRefs.current[row._id] = el)}
                              />
                              {/* mobile view */}
                            </div>

                            <div className="outer-right-sec-overview">
                              <div className="overview-sec-timer-inner-right">
                                {/* Mobile view */}
                                <div
                                  className="project-sprint-outer col project-sprint-outer-mobile"
                                  style={{ display: "none" }}
                                  ref={projectEditDropdownRef}
                                >
                                  {row._id === editedId && (
                                    <>
                                      <Col
                                        className="cursor"
                                        onClick={handleProjectDropdownToggle}
                                      >
                                        <span
                                          className={`tooltip-tracker ${getTextSizeClassName(
                                            projectNameEdit,
                                            sprintNameEdit,
                                            clientEdit
                                          )}`}
                                        >
                                          {projectNameEdit}
                                          {`${sprintNameEdit ? ` - ${sprintNameEdit}` : " "}`}
                                          <input type="hidden" name="project" value={projectIdEdit} />
                                          <div className="project-name-tracker">
                                            {clientEdit}
                                          </div>
                                        </span>
                                        <span
                                          className={`project-name-edit-inner ${getTextSizeClassName(
                                            projectNameEdit,
                                            sprintNameEdit,
                                            clientEdit
                                          )}`}
                                          style={{
                                            display: "inline-block",
                                            overflow: "hidden",
                                            whiteSpace: "nowrap",
                                            textOverflow: "ellipsis",
                                            height: "22px",
                                            maxWidth: "140px",
                                          }}
                                        >
                                          {projectNameEdit}
                                          {`${sprintNameEdit ? ` - ${sprintNameEdit}` : ""}`}
                                          <input type="hidden" name="project" value={projectIdEdit} />
                                          <div className="project-name-tracker">
                                            {clientEdit}
                                          </div>
                                        </span>
                                      </Col>

                                      <ProjectDropdown
                                        isOpen={projectEditDropdownOpen}
                                        onToggle={handleProjectDropdownToggle}
                                        filter={filter}
                                        onFilterChange={handleProjectFilterChange}
                                        filteredClientData={filteredClientData}
                                        selectedClients={selectedClients}
                                        onClientToggle={handleClientToggle}
                                        selectedProject={selectedProject}
                                        onProjectSelect={handleProjectSelect}
                                        onProjectToggle={handleProjectToggle}
                                        onSprintSelect={handleSprintSelect}
                                        projectNameEdit={projectNameEdit}
                                        sprintNameEdit={sprintNameEdit}
                                        clientEdit={clientEdit}
                                        projectIdEdit={projectIdEdit}
                                        currentTextCount={currentTextCount}
                                        isMobile={true}
                                        contentRef={projectDropdownContentRef}
                                      />
                                    </>
                                  )}

                                  {row._id !== editedId && (
                                    <ProjectDisplay
                                      project={row.project}
                                      sprint={row.sprint}
                                      client={row.client}
                                      currentTextCount={currentTextCount}
                                      onClick={() => {
                                        initializeEditState(row, editStateSetters, formatTime);
                                      }}
                                      isMobile={true}
                                    />
                                  )}
                                </div>

                                {/* end mobile */}

                                {/* tag , timer section */}
                                <div className="tag-section-overview-outer" ref={tagEditDropdownRef}>
                                  {row._id === editedId && (
                                    <>
                                      <TagDisplay
                                        tag={row.tag}
                                        tagEdit={tagEdit}
                                        onClick={(e) => handleTagDropdownToggle(e)}
                                      />
                                      <TagDropdown
                                        isOpen={tagEditDropdownOpen}
                                        tagEdit={tagEdit}
                                        tagQuery={tagQuery}
                                        onTagInputChange={handleTagInputChange}
                                        filteredTagItems={filteredTagItems}
                                        onTagSelect={handleTagSelect}
                                        contentRef={tagDropdownContentRef}
                                      />
                                    </>
                                  )}
                                  {row._id !== editedId && (
                                    <TagDisplay
                                      tag={row.tag}
                                      onClick={() => {
                                        initializeEditState(row, editStateSetters, formatTime);
                                      }}
                                    />
                                  )}
                                  <input type="hidden" name="tag" value={tagIdEdit} />
                                </div>
                                <div className="billable-section">
                                  <BillableIcon isBillable={editedId === row._id ? billableEdit : row.billable} />
                                </div>
                                <div className={`overview-sec-timer-date-time date-${row._id}`} style={{ position: "relative" }} >
                                  <Col>
                                    {row._id === editedId ? (
                                      <>
                                        <div onClick={(e) => e.stopPropagation()}>
                                          <TimeInputs
                                            timeFormat={timeFormat}
                                            startTimeEdit24={startTimeEdit24}
                                            endTimeEdit24={endTimeEdit24}
                                            startTimeEdit={startTimeEdit}
                                            endTimeEdit={endTimeEdit}
                                            onStartTimeChange24={(e) => {
                                              if (validateNoAlphabets(e.target.value)) {
                                                setStartTimeEdit24(e.target.value);
                                              }
                                            }}
                                            onEndTimeChange24={(e) => {
                                              if (validateNoAlphabets(e.target.value)) {
                                                setEndTimeEdit24(e.target.value);
                                              }
                                            }}
                                            onStartTimeChange={(e) => setStartTimeEdit(e.target.value)}
                                            onEndTimeChange={(e) => setEndTimeEdit(e.target.value)}
                                            onStartTimeBlur={() => {
                                              if (!startTimeEdit) setStartTimeEdit(DEFAULT_START_TIME[timeFormat] || DEFAULT_START_TIME[12]);
                                              setIsTimeInputActive(false);
                                            }}
                                            onEndTimeBlur={() => {
                                              if (!endTimeEdit) setEndTimeEdit(DEFAULT_END_TIME[timeFormat] || DEFAULT_END_TIME[12]);
                                              setIsTimeInputActive(false);
                                            }}
                                            onStartTimeFocus={() => setIsTimeInputActive(true)}
                                            onEndTimeFocus={() => setIsTimeInputActive(true)}
                                            uStartTimeError={uStartTimeError}
                                            uEndTimeError={uEndTimeError}
                                            startInputRef={startRowInputRef}
                                            endInputRef={endRowInputRef}
                                            start12TimeInputRef={start12TimeRowInputRef}
                                            end12TimeInputRef={end12TimeRowInputRef}
                                            editedId={editedId}
                                            groupId={row._id}
                                          />
                                        </div>

                                        <div className="date-overview-sec date-overview-edit" onClick={(e) => e.stopPropagation()}>
                                          <DatePickerIcon onClick={handleDateIconClick} />
                                          <DatePicker
                                            block
                                            name="date"
                                            appearance="subtle"
                                            value={
                                              new Date(zoneConverter(dateEdit, global.config.getTimeZone(), "YYYY-MM-DD HH:mm:ss"))
                                            }
                                            onChange={(value) => {
                                              setDateOldEdit(new Date(zoneConverter(dateEdit, global.config.getTimeZone(), "YYYY-MM-DD HH:mm:ss")));
                                              setDateEdit(value);
                                            }}
                                            ref={datePickerRef}
                                            placement="bottomEnd"
                                            oneTap
                                            isoWeek={appSettings.weekStartDay && appSettings.weekStartDay === "Monday"}
                                          />
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        <TimeDisplay
                                          startTime={row.startTime}
                                          endTime={row.endTime}
                                          timeFormat={timeFormat}
                                          onClick={() => {
                                            initializeEditState(row, editStateSetters, formatTime);
                                            setIsTimeInputActive(true);
                                          }}
                                        />
                                        <div
                                          className="date-overview-sec"
                                          onClick={() => {
                                            initializeEditState(row, editStateSetters, formatTime);
                                          }}
                                        >
                                          <DatePickerIcon style={{ cursor: "pointer" }} />
                                        </div>
                                      </>
                                    )}
                                  </Col>
                                </div>

                                <div className="overview-time-edit">
                                  <Col>
                                    <div className="overview-time-edit-inner">
                                      {/* duration edit section */}
                                      {row._id === editedId && (
                                        <div onClick={(e) => e.stopPropagation()}>
                                          <DurationInput
                                            value={uFormatedTime}
                                            onChange={(e) => timeShowEdit(e.target.value)}
                                            error={uFormatedTimeError}
                                          />
                                        </div>
                                      )}

                                      {/* duration edit section end */}

                                      {row._id !== editedId && (
                                        <DurationDisplay
                                          duration={row.duration}
                                          formatTime={formatTime}
                                          onClick={() => {
                                            initializeEditState(row, editStateSetters, formatTime);
                                          }}
                                        />
                                      )}
                                    </div>

                                    {row._id === editedId && (
                                      <UpdateButton
                                        isLoading={isUpdateLoading}
                                        disabled={
                                          isUpdateLoading ||
                                          uFormatedTimeError ||
                                          uEndTimeError ||
                                          uStartTimeError ||
                                          isTimeInputActive
                                        }
                                        isTimeInputActive={isTimeInputActive}
                                      />
                                    )}

                                    {row._id !== editedId && (
                                      <ActionsMenu
                                        itemId={row._id}
                                        isOpen={activeDropdownId === row._id}
                                        onToggle={() => handleDropdownToggle(row._id)}
                                        onDuplicate={() => handleDuplicate(row)}
                                        onDelete={() => handleDelete(row._id, group)}
                                        duplicateLoading={duplicateLoading}
                                        deleteLoading={isDeleteLoading}
                                        deleteId={deleteId}
                                        refCallback={(el) => (activedropdownRefs.current[row._id] = el)}
                                      />
                                    )}
                                  </Col>
                                </div>
                              </div>
                            </div>
                          </div>
                        </React.Fragment>
                      ))}
                  </div>
                </>
              </form>
            </React.Fragment>
          ))}
      </>
    </Card.Body>
  );
};

export default CardBodyImportData;
